// src/components/Layout/Sidebar.tsx - VERSIÓN FINAL CON TODAS LAS RUTAS
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, ClipboardList, Settings, ChevronDown, ShoppingBag, 
  Menu, X, Building2, CreditCard, DollarSign, UserCog, FileText, Dumbbell,
  Activity, LogOut, Calendar, Receipt, Cog, FolderCog, User, TrendingUp,
  Wallet, ArrowUpRight, CheckCircle, RefreshCw, Zap, Database,
  UserCheck, QrCode, HelpCircle, Package, Cake, Clock, Globe,
  Sun, Moon, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getImageUrl } from '../../services/apiClient';

interface NavItemProps {
  icon: React.ReactNode;
  text: string;
  active: boolean;
  onClick: () => void;
  badge?: string | number;
  isNew?: boolean;
  collapsed?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ icon, text, active, onClick, badge, isNew }) => {
  const { isCollapsed } = useTheme();
  return (
    <button
      className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'} py-2 rounded-md transition-colors relative w-full ${
        active 
          ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-semibold' 
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700/60 hover:text-gray-900 dark:hover:text-white'
      }`}
      onClick={onClick}
      title={text}
    >
      <div className="flex items-center">
        <div className={isCollapsed ? '' : 'mr-3'}>{icon}</div>
        {!isCollapsed && (
          <span className={`text-sm font-medium ${active ? 'font-semibold' : ''}`}>{text}</span>
        )}
        {!isCollapsed && isNew && (
          <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-green-500 text-white rounded-full">
            NEW
          </span>
        )}
      </div>
      {!isCollapsed && badge && (
        <span className="px-2 py-0.5 ml-auto text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-300">
          {badge}
        </span>
      )}
    </button>
  );
};

interface DropdownNavProps {
  icon: React.ReactNode;
  text: string;
  active: boolean;
  children: React.ReactNode;
  isNew?: boolean;
}

const DropdownNav: React.FC<DropdownNavProps> = ({ icon, text, active, children, isNew }) => {
  const { isCollapsed } = useTheme();
  const [isOpen, setIsOpen] = useState(active);

  React.useEffect(() => {
    if (active) setIsOpen(true);
  }, [active]);

  return (
    <div>
      <button
        className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-3'} py-2 rounded-md transition-colors relative w-full ${
          active 
            ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-semibold' 
            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700/60 hover:text-gray-900 dark:hover:text-white'
        }`}
        onClick={() => setIsOpen(!isOpen)}
        title={text}
      >
        <div className="flex items-center">
          <div className={isCollapsed ? '' : 'mr-3'}>{icon}</div>
          {!isCollapsed && (
            <span className={`text-sm font-medium ${active ? 'font-semibold' : ''}`}>{text}</span>
          )}
          {!isCollapsed && isNew && (
            <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-green-500 text-white rounded-full">
              NEW
            </span>
          )}
        </div>
        {!isCollapsed && (
          <ChevronDown
            size={16}
            className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {isOpen && (
        <div className={`${isCollapsed ? 'ml-0 pl-1 border-l-2 border-blue-500' : 'ml-4'} mt-1 space-y-1`}>
          {children}
        </div>
      )}
    </div>
  );
};

const Sidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, gymData, userRole, subscriptionTier, hasModule, logout } = useAuth();
  const { theme, toggleTheme, isCollapsed, toggleSidebar } = useTheme();

  React.useEffect(() => {
    setLogoError(false);
  }, [gymData?.logo]);

  const rawLogo = gymData?.logo;
  const logoUrl = rawLogo ? getImageUrl(rawLogo) : '';
  const showLogo = logoUrl && !logoError;

  // Función para determinar si una ruta está activa
  const isActive = (path: string): boolean => {
    return location.pathname === `/${path}` || location.pathname.startsWith(`/${path}/`);
  };

  // Función para navegar
  const handleNavigate = (path: string) => {
    if (path === 'members') {
      navigate('/members', { state: { resetView: Date.now() } });
    } else {
      navigate(`/${path}`);
    }
    setIsOpen(false); // Cerrar sidebar en móvil
  };

  // Función para cerrar sesión
  const handleLogout = async () => {
    try {
      if (logout) logout();
      const savedTheme = localStorage.getItem('theme');
      const savedCollapsed = localStorage.getItem('sidebar_collapsed');
      localStorage.clear();
      if (savedTheme) localStorage.setItem('theme', savedTheme);
      if (savedCollapsed) localStorage.setItem('sidebar_collapsed', savedCollapsed);
      await auth.signOut().catch(() => {});
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      window.location.href = '/login';
    }
  };


  // Funciones auxiliares para determinar estados activos de grupos
const isMembershipManagementActive = (): boolean => {
  return isActive('settings/memberships') || isActive('payments');
};

  const isFinancialActive = (): boolean => {
    return isActive('dashboard-financial') || isActive('cashier');
  };

  const isExercisesActive = (): boolean => {
    return isActive('exercises') || isActive('routines') || isActive('member-routines');
  };

  const isSettingsActive = (): boolean => {
    return isActive('settings/business') || isActive('settings/activities') || isActive('settings/users');
  };

  const isSuperadminActive = (page: string): boolean => {
    return location.pathname.includes(`/superadmin/${page}`);
  };

  return (
    <>
      {/* Botón de menú móvil */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border"
      >
        <Menu size={24} />
      </button>

      {/* Overlay móvil */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 ${isCollapsed ? 'w-20' : 'w-64'} bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 transform transition-all duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 flex flex-col justify-between shadow-sm`}>
        
        {/* Header móvil */}
        <div className="flex justify-between items-center md:hidden px-4 pt-4">
          <h2 className="text-xl font-bold dark:text-white">GymSystem</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-md text-gray-500 hover:text-gray-800 dark:text-gray-300"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Header con Logo y botón de colapso */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0 relative bg-white dark:bg-slate-800">
          {/* Botón para colapsar en Desktop */}
          <button
            onClick={toggleSidebar}
            className="hidden md:flex absolute top-3 right-3 p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors z-10"
            title={isCollapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>

          {!isCollapsed ? (
            <div className="flex flex-col items-center pt-1 pr-4">
              {showLogo ? (
                <div className="relative mb-2">
                  <img 
                    src={logoUrl} 
                    alt="" 
                    onError={() => setLogoError(true)}
                    className="h-16 w-16 object-cover rounded-full border-2 border-blue-500 shadow-md"
                  />
                </div>
              ) : (
                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/60 rounded-full flex items-center justify-center mb-2 shadow-md">
                  <Building2 size={24} className="text-blue-600 dark:text-blue-400" />
                </div>
              )}
              <h1 className="text-sm font-bold text-gray-800 dark:text-white text-center truncate max-w-[170px]">
                {userRole === 'superadmin' ? 'MultiGym Admin' : (gymData?.name || "GymSystem")}
              </h1>
            </div>
          ) : (
            <div className="flex flex-col items-center pt-6">
              {showLogo ? (
                <img 
                  src={logoUrl} 
                  alt="" 
                  onError={() => setLogoError(true)}
                  className="h-9 w-9 object-cover rounded-full border-2 border-blue-500 shadow-sm"
                />
              ) : (
                <div className="h-9 w-9 bg-blue-100 dark:bg-blue-900/60 rounded-full flex items-center justify-center shadow-sm">
                  <Building2 size={20} className="text-blue-600 dark:text-blue-400" />
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Navegación */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <nav className="space-y-1">
            {/* Panel de Superadmin */}
            {userRole === 'superadmin' && (
              <>
                <div className="py-2">
                  <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wider px-3 mb-2">
                    Panel de Administración
                  </h3>
                  
                  <NavItem
                    icon={<LayoutDashboard size={20} />}
                    text="Dashboard"
                    active={isSuperadminActive('dashboard')}
                    onClick={() => handleNavigate('superadmin/dashboard')}
                  />
                  
                  <NavItem
                    icon={<Building2 size={20} />}
                    text="Gimnasios"
                    active={isSuperadminActive('gyms')}
                    onClick={() => handleNavigate('superadmin/gyms')}
                  />
                  
                  <NavItem
                    icon={<CreditCard size={20} />}
                    text="Suscripciones"
                    active={isSuperadminActive('subscriptions')}
                    onClick={() => handleNavigate('superadmin/subscriptions')}
                  />
                  
                  <NavItem
                    icon={<DollarSign size={20} />}
                    text="Ingresos"
                    active={isSuperadminActive('revenue')}
                    onClick={() => handleNavigate('superadmin/revenue')}
                  />

                    <NavItem
                      icon={<Clock size={20} />}
                      text="Pagos Pendientes"
                      active={isSuperadminActive('pending-payments')}
                      onClick={() => handleNavigate('superadmin/pending-payments')}
                    />
                  
                  <NavItem
                    icon={<Dumbbell size={20} />}
                    text="Ejercicios Globales"
                    active={isSuperadminActive('exercises')}
                    onClick={() => handleNavigate('superadmin/exercises')}
                  />
                  
                  <NavItem
                    icon={<Globe size={20} />}
                    text="Configurar Landing"
                    active={isSuperadminActive('landing')}
                    onClick={() => handleNavigate('superadmin/landing')}
                  />
                </div>
                
                <div className="border-t border-gray-200 my-2"></div>
              </>
            )}
            
            {/* Navegación para Admin y User */}
            {(userRole === 'admin' || userRole === 'user') && (
              <>
                <NavItem
                  icon={<LayoutDashboard size={20} />}
                  text="Dashboard"
                  active={isActive('dashboard')}
                  onClick={() => handleNavigate('dashboard')}
                />
                
                <NavItem
                  icon={<Users size={20} />}
                  text="Socios"
                  active={isActive('members')}
                  onClick={() => handleNavigate('members')}
                />
                
                {hasModule('attendance') && (
                  <div className="flex items-center group relative">
                    <div className="flex-1">
                      <NavItem
                        icon={<Calendar size={20} />}
                        text="Asistencias"
                        active={isActive('attendance')}
                        onClick={() => handleNavigate('attendance')}
                      />
                    </div>
                    <button 
                      onClick={() => window.open('/kiosk', '_blank')}
                      className="absolute right-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Abrir Portal de Ingreso"
                    >
                      <ArrowUpRight size={18} />
                    </button>
                  </div>
                )}
                <NavItem
                    icon={<CreditCard size={20} />}
                    text="Membresías y Cobros"
                    active={isActive('payments') || isActive('membership-control')}
                    onClick={() => handleNavigate('payments')}
                  />  
                    {hasModule('daily_cash') && (
                  <NavItem
                    icon={<ShoppingBag size={16} />}
                    text="Caja Diaria"
                    active={isActive('cashier')}
                    onClick={() => handleNavigate('cashier')}
                  />
                )}
                      
                 

                  




                {hasModule('products') && (
                  <NavItem
                    icon={<Package size={20} />}
                    text="Productos"
                    active={isActive('products')}
                    onClick={() => handleNavigate('products')}
                  />
                )}
                <NavItem
                  icon={<Cake size={20} />}
                  text="Cumpleaños"
                  active={isActive('birthdays')}
                  onClick={() => handleNavigate('birthdays')}
                />
                {hasModule('classes') && (
                  <NavItem
                    icon={<Calendar size={20} />}
                    text="Clases"
                    active={isActive('classes')}
                    onClick={() => handleNavigate('classes')}
                  />
                )}
           
                
               
                
                
                
                {hasModule('reports') && userRole === 'admin' && (
                  <DropdownNav
                    icon={<Wallet size={20} />}
                    text="Reportes"
                    active={isFinancialActive()}
                  >
                    <div className="space-y-1 py-2">
                      <NavItem
                        icon={<Receipt size={20} />}
                        text="Informes"
                        active={isActive('reports')}
                        onClick={() => handleNavigate('reports')}
                      />
                      <NavItem
                        icon={<TrendingUp size={16} />}
                        text="Dashboard Financiero"
                        active={isActive('dashboard-financial')}
                        onClick={() => handleNavigate('dashboard-financial')}
                      />
                    </div>
                  </DropdownNav>
                )}
                
                {hasModule('routines') && (
                  <DropdownNav
                    icon={<Dumbbell size={20} />}
                    text="Ejercicios"
                    active={isExercisesActive()}
                  >
                    <div className="space-y-1 py-2">
                      <NavItem
                        icon={<Activity size={16} />}
                        text="Ejercicios"
                        active={isActive('exercises')}
                        onClick={() => handleNavigate('exercises')}
                      />
                      <NavItem
                        icon={<ClipboardList size={16} />}
                        text="Rutinas"
                        active={isActive('routines')}
                        onClick={() => handleNavigate('routines')}
                      />
                      <NavItem
                        icon={<Users size={16} />}
                        text="Rutinas de Socios"
                        active={isActive('member-routines')}
                        onClick={() => handleNavigate('member-routines')}
                      />
                    </div>
                  </DropdownNav>
                )}


                {hasModule('qr_registration') && userRole === 'admin' && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    
                    <div className="py-2">
                      <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wider px-3 mb-2">
                        Auto-Registro
                      </h3>
                      
                      <NavItem
                        icon={<UserCheck size={20} />}
                        text="Registros Pendientes"
                        active={isActive('pending-registrations')}
                        onClick={() => handleNavigate('pending-registrations')}
                        isNew={false}
                      />
                      
                      <NavItem
                        icon={<QrCode size={20} />}
                        text="Código QR"
                        active={isActive('qr-generator')}
                        onClick={() => handleNavigate('qr-generator')}
                        isNew={false}
                      />
                    </div>
                  </>
                )}

                {/* ✅ NUEVAS OPCIONES - Control de Datos y Membresías */}
                {userRole === 'admin' && (
                  <>
                    <div className="border-t border-gray-200 my-2"></div>
                    
                    <div className="py-2">
                      <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wider px-3 mb-2">
                        Control de Datos
                      </h3>
                      
                      <NavItem
                        icon={<RefreshCw size={20} />}
                        text="Actualizaciones"
                        active={isActive('update-tracker')}
                        onClick={() => handleNavigate('update-tracker')}
                        isNew={false}
                      />
                      
                    </div>
                  </>
                )}
                
                {/* Configuración - Solo para admins */}
                {userRole === 'admin' && (
                  <DropdownNav
                    icon={<Cog size={20} />}
                    text="Configuración"
                    active={isSettingsActive()}
                  >
                    <div className="space-y-1 py-2">
                      <NavItem
                        icon={<Building2 size={16} />}
                        text="Perfil del Negocio"
                        active={isActive('settings/business')}
                        onClick={() => handleNavigate('settings/business')}
                      />
                      <NavItem
                        icon={<Activity size={16} />}
                        text="Actividades"
                        active={isActive('settings/activities')}
                        onClick={() => handleNavigate('settings/activities')}
                      />
                     
                      <NavItem
                        icon={<Settings size={16} />}
                        text="Configurar Membresías"
                        active={isActive('settings/memberships')}
                        onClick={() => handleNavigate('settings/memberships')}
                      />


                
                    
                      <NavItem
                        icon={<User size={16} />}
                        text="Usuarios"
                        active={isActive('settings/users')}
                        onClick={() => handleNavigate('settings/users')}
                      />
                    </div>

                    {/* ✅ NUEVO - Centro de Ayuda */}
                    <NavItem
                      icon={<HelpCircle size={20} />}
                      text="Centro de Ayuda"
                      active={isActive('help')}
                      onClick={() => handleNavigate('help')}
                    />

              
                  </DropdownNav>

                  
                )}
                
              </>
            )}
          </nav>



        {/* Footer con toggle Modo Oscuro y Logout */}
        <div className="border-t border-gray-200 dark:border-slate-700 px-3 py-3 flex-shrink-0 bg-gray-50 dark:bg-slate-800/80">
          {/* Toggle Dark Mode */}
          <button
            onClick={toggleTheme}
            className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} w-full px-3 py-2 mb-2 rounded-lg bg-gray-200/70 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300/80 dark:hover:bg-slate-600 transition-colors text-xs font-semibold`}
            title={theme === 'dark' ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
          >
            <div className="flex items-center">
              {theme === 'dark' ? (
                <Sun size={18} className="text-amber-400 mr-2" />
              ) : (
                <Moon size={18} className="text-indigo-600 mr-2" />
              )}
              {!isCollapsed && (
                <span>{theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}</span>
              )}
            </div>
            {!isCollapsed && (
              <span className="text-[10px] px-1.5 py-0.5 bg-gray-300 dark:bg-slate-600 rounded text-gray-600 dark:text-gray-300 font-mono">
                {theme === 'dark' ? 'ON' : 'OFF'}
              </span>
            )}
          </button>

          {!isCollapsed && userData?.name && (
            <div className="mb-2 px-1 text-[11px] text-gray-500 dark:text-gray-400 truncate">
              <div>Conectado: <span className="font-medium text-gray-700 dark:text-gray-200">{userData.name}</span></div>
            </div>
          )}
          
          <button
            onClick={handleLogout}
            className={`flex items-center ${isCollapsed ? 'justify-center' : ''} px-3 py-2 w-full text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-md transition-colors`}
            title="Cerrar Sesión"
          >
            <LogOut size={20} className={isCollapsed ? '' : 'mr-3'} />
            {!isCollapsed && <span className="text-sm font-medium">Cerrar Sesión</span>}
          </button>
        </div>
        </div>
        
        
      </div>
    </>
  );
};

export default Sidebar;